import { useMemo, useState } from 'react'

import { useAddMember, useCandidates } from '../../hooks/queries'
import type { Candidate } from '../../api/types'
import { CheckIcon, XIcon } from '../../components/Icons'
import { initials } from '../../lib/format'
import { useTexte, WORTLAUT } from '../../texte'
import { useEscape } from '../../hooks/useEscape'

/**
 * Personen einer Reise zuordnen — ohne Einladungslink (#36).
 *
 * Aus dem Betrieb: „wenn jemand generell registriert ist, soll diese Person
 * ohne Zusendung eines weiteren Links in der Verwaltungsoberfläche der Reise
 * zugeordnet werden können. […] über eine Ankreuzleiste können diese Personen
 * zugeordnet werden." Der Umweg über das Postfach entfällt damit für jeden,
 * der ohnehin schon ein Konto hat.
 *
 * **Das Häkchen wirkt sofort, es gibt keinen Speichern-Knopf.** Ein Häkchen,
 * das erst später wirkt, ist genau die stille Absage, die dieses Projekt
 * nicht will — man tippt, und es passiert nichts Sichtbares.
 *
 * **Und es geht nur in eine Richtung.** Herausnehmen steht in der
 * Empfängerliste und fragt dort nach; ein Häkchen, das beim zweiten Tippen
 * jemanden aus der Reise wirft, wäre eine zerstörende Handlung ohne
 * Rückfrage. Wer schon dabei ist, steht hier als *dabei* und ist ausgegraut.
 *
 * **Namen, keine E-Mail-Adressen.** Der Server liefert keine — wer eine Reise
 * besitzt, sieht mit dieser Liste die Namen aller registrierten Konten, und
 * daraus soll kein Adressverzeichnis werden.
 */
export function Zuordnungsblatt({ tripId, onZu }: { tripId: number; onZu: () => void }) {
  const texte = useTexte()
  const t = texte.reise.zuordnen
  const kandidaten = useCandidates(tripId, true)
  const zuordnen = useAddMember(tripId)
  const [suche, setSuche] = useState('')
  // Welche Zeile gerade läuft — damit die Rückmeldung an ihr steht und nicht
  // an allen. `isPending` allein wüsste nur, dass irgendetwas läuft.
  const [laeuft, setLaeuft] = useState<number | null>(null)

  // Escape sagt Nein — wie in `Rueckfrage` und in jedem anderen Blatt.
  useEscape(onZu)

  const gefiltert = useMemo(() => {
    const wort = suche.trim().toLocaleLowerCase('de')
    const alle = kandidaten.data ?? []
    if (wort === '') return alle
    return alle.filter((k) => k.user.display_name.toLocaleLowerCase('de').includes(wort))
  }, [kandidaten.data, suche])

  const offene = gefiltert.filter((k) => k.stand !== 'dabei').length

  return (
    <div
      className="zuordnung"
      role="dialog"
      aria-modal="true"
      aria-label={t.titel}
      onClick={onZu}
    >
      <div className="zuordnung__blatt" onClick={(event) => event.stopPropagation()}>
        <div className="zuordnung__kopf">
          <h2 className="card__titel card__titel--dicht">
            {t.titel}
          </h2>
          <button
            type="button"
            className="icon-button"
            onClick={onZu}
            aria-label={t.schliessen}
            title={texte.allgemein.schliessen}
          >
            <XIcon size={18} />
          </button>
        </div>

        <p className="feld__hinweis feld__hinweis--13 feld__hinweis--nah">
          {t.lead}
        </p>

        {/* Ein Suchfeld über den eigenen Bestand: es geht nichts nach außen,
            gefiltert wird die Liste, die schon da ist. */}
        <input
          className="zuordnung__feld"
          type="search"
          value={suche}
          placeholder={t.filtern}
          aria-label={t.filtern}
          onChange={(event) => setSuche(event.target.value)}
        />

        {kandidaten.isLoading && (
          <p className="feld__hinweis feld__hinweis--luft">
            {t.laedt}
          </p>
        )}
        {kandidaten.isError && (
          <p className="notice" role="alert">
            {kandidaten.error.message}
          </p>
        )}
        {zuordnen.isError && (
          <p className="notice" role="alert">
            {zuordnen.error.message}
          </p>
        )}

        {kandidaten.isSuccess && gefiltert.length === 0 && (
          <p className="feld__hinweis feld__hinweis--luft">
            {suche.trim() === '' ? t.niemand : t.keinTreffer}
          </p>
        )}

        {gefiltert.length > 0 && (
          <ul className="zuordnung__liste">
            {gefiltert.map((k) => (
              <li key={k.user.id}>
                <Zeile
                  kandidat={k}
                  laeuft={laeuft === k.user.id && zuordnen.isPending}
                  onZuordnen={() => {
                    setLaeuft(k.user.id)
                    zuordnen.mutate(k.user.id)
                  }}
                />
              </li>
            ))}
          </ul>
        )}

        {/* Wie viele überhaupt noch dazukommen könnten — sonst zählt man
            die Zeilen mit dem Finger nach. */}
        {kandidaten.isSuccess && gefiltert.length > 0 && (
          <p className="feld__hinweis feld__hinweis--13 feld__hinweis--weiter">
            {offene === 0 ? t.alleDabei : t.offene(offene)}
          </p>
        )}
      </div>
    </div>
  )
}

/** Was neben dem Namen steht — vom Server entschieden, hier nur benannt. */
const STAND: Record<Candidate['stand'], string> = WORTLAUT.reise.zuordnen.stand

function Zeile({
  kandidat,
  laeuft,
  onZuordnen,
}: {
  kandidat: Candidate
  laeuft: boolean
  onZuordnen: () => void
}) {
  const t = useTexte().reise.zuordnen
  const dabei = kandidat.stand === 'dabei'
  const hinweis = STAND[kandidat.stand]

  return (
    <button
      type="button"
      className="zuordnung__zeile"
      disabled={dabei || laeuft}
      aria-label={t.zuordnenLabel(kandidat.user.display_name)}
      onClick={onZuordnen}
    >
      <span className="avatar avatar--winzig">
        {initials(kandidat.user.display_name)}
      </span>
      <span className="zuordnung__name">
        {kandidat.user.display_name}
        {hinweis !== '' && <span className="zuordnung__stand">{hinweis}</span>}
      </span>
      {/* Das Kästchen ist kein `input[type=checkbox]`: die ganze Zeile ist der
          Knopf, und ein zweites fokussierbares Element darin wäre für die
          Tastatur eine Falle. Gezeichnet wird es trotzdem wie eines. */}
      <span className={`zuordnung__haken${dabei ? ' zuordnung__haken--an' : ''}`}>
        {dabei && <CheckIcon size={15} />}
        {laeuft && '…'}
      </span>
    </button>
  )
}
